import type {
	AiAssistHostValue,
	AiAssistPlanOperation,
} from "@southfields-digital/mxmz-react-ai-assist/graphics";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import { useRundownLayout } from "src/components/RundownSettings/useRundownLayout";
import { isAutoUpdateEnabled } from "src/features/dataConnector/application/autoUpdateManager";
import type { StateType } from "src/redux/reducers";
import { actions as aiAssistActions } from "src/redux/reducers/aiAssist";
import { REQUEST_GET_ALL_GRAPHICS_BY_RUNDOWN_ID } from "src/redux/reducers/graphic";
import { NOTIFY } from "src/redux/reducers/notification";
import {
	actions as playlistGraphicActions,
	REQUEST_GET_ALL_PLAYLIST_GRAPHICS,
	REQUEST_SET_SELECTED_PLAYLIST_GRAPHIC,
} from "src/redux/reducers/playlistGraphic";
import { applyPlanWith } from "./applyPlan";

/**
 * Everything Ask needs from this app. Reads come off the store as-is — the package derives
 * the rundown context from them — and writes go down the same routes a click uses.
 */
export const useAiAssistHost = (): AiAssistHostValue => {
	const dispatch = useDispatch();
	const store = useStore<StateType>();
	const { layout } = useRundownLayout();

	const roles = useSelector((state: StateType) => state.user.auth.roles);
	const { data: rundown } = useSelector((state: StateType) => state.rundown.detail);
	const { data: playlists, selectedId: activePlaylistId } = useSelector(
		(state: StateType) => state.playlist.getAll,
	);
	const { playlistGraphics, selectedPlaylistGraphicId } = useSelector(
		(state: StateType) => state.playlistGraphic,
	);
	const { data: templates, error: templatesError } = useSelector(
		(state: StateType) => state.graphic.allFromRundown,
	);

	const requestTemplates = useCallback(
		(rundownId: string) => {
			dispatch({ type: REQUEST_GET_ALL_GRAPHICS_BY_RUNDOWN_ID, payload: { rundownId } });
		},
		[dispatch],
	);

	const selectReference = useCallback(
		(playlistGraphicId: string) => {
			const { playlistGraphics, selectedPlaylistGraphic } = store.getState().playlistGraphic;
			const playlistGraphic = playlistGraphics?.find((graphic) => graphic.id === playlistGraphicId);

			if (!playlistGraphic) return;

			dispatch({
				type: REQUEST_SET_SELECTED_PLAYLIST_GRAPHIC,
				payload: { playlistGraphic, selectedPlaylistGraphic },
			});
		},
		[dispatch, store],
	);

	const applyPlan = useCallback(
		async (operations: AiAssistPlanOperation[]) => {
			// Read at call time, not from the render that mounted this: a write resolves long
			// after that render and would otherwise splice into a stale order.
			const state = store.getState();
			const playlistId = state.playlist.getAll.selectedId;

			if (!playlistId) {
				return {
					applied: [],
					updated: [],
					written: [],
					hasPlaced: false,
					failure: "there is no active playlist to apply these to",
				};
			}

			const outcome = await applyPlanWith(operations, {
				playlistId,
				playlistGraphics: state.playlistGraphic.playlistGraphics ?? [],
				templates: state.graphic.allFromRundown.data ?? [],
			});

			if (outcome.updated.length > 0) {
				dispatch(playlistGraphicActions.receiveUpdatePlaylistGraphics(outcome.updated));
			}

			// After the values, never before: the form re-derives on this marker and would
			// otherwise rebuild itself from what was there a moment ago.
			if (outcome.written.length > 0) dispatch(aiAssistActions.receiveAiWrites(outcome.written));

			// The store only replaces graphics it already knows, so a creation needs one read to
			// land — which also settles the order after a placement.
			if (outcome.hasPlaced && state.rundown.detail.data) {
				dispatch({
					type: REQUEST_GET_ALL_PLAYLIST_GRAPHICS,
					payload: { rundownId: state.rundown.detail.data.id },
				});
			}

			if (outcome.failure) {
				dispatch({
					type: NOTIFY,
					payload: {
						variant: "warning",
						title:
							outcome.applied.length > 0
								? "Some of the changes were not applied"
								: "Nothing could be applied",
						description: outcome.failure,
					},
				});
			}

			return outcome;
		},
		[dispatch, store],
	);

	return useMemo(
		() => ({
			rundown: rundown ?? null,
			playlists: playlists ?? [],
			activePlaylistId,
			playlistGraphics: playlistGraphics ?? [],
			selectedPlaylistGraphicId,
			templates: templates ?? [],
			templatesSettled: Boolean(templates) || Boolean(templatesError),
			layout,
			roles: roles ?? null,
			isAutoUpdating: isAutoUpdateEnabled,
			requestTemplates,
			applyPlan,
			selectReference,
		}),
		[
			rundown,
			playlists,
			activePlaylistId,
			playlistGraphics,
			selectedPlaylistGraphicId,
			templates,
			templatesError,
			layout,
			roles,
			requestTemplates,
			applyPlan,
			selectReference,
		],
	);
};
